import React, { useState, useEffect } from 'react';
import {
  getProjects,
  addProject,
  updateProject,
  deleteProject,
  addProjectResponsibility,
  addProjectTechnology,
  deleteProjectResponsibility,
  deleteProjectTechnology
} from '../../services/profile';
import { FaEdit, FaTrashAlt, FaPlus, FaTimes, FaSave, FaCode, FaExternalLinkAlt } from 'react-icons/fa';
import './Projects.css';

const Projects = ({ profileSection, profileData, setCompletion }) => {
  const [projectsList, setProjectsList] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // State for new/editing project
  const [projectForm, setProjectForm] = useState({
    project_title: '',
    start_date: '',
    end_date: '',
    is_current: false,
    technologies: '',
    responsibilities: '',
    url: ''
  });

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const response = await getProjects(profileData?.profile_id);
        setProjectsList(response);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setFormError('Failed to load projects. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    setCompletion("projects", projectsList);
  }, [projectsList])

  // Clear form data
  const clearForm = () => {
    setProjectForm({
      project_title: '',
      start_date: '',
      end_date: '',
      is_current: false,
      technologies: '',
      responsibilities: '',
      url: ''
    });
    setFormError('');
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProjectForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // If setting current project to true, clear end date
    if (name === 'is_current' && checked) {
      setProjectForm(prev => ({
        ...prev,
        end_date: ''
      }));
    }
  };

  // Handle responsibilities input
  const handleResponsibilitiesChange = (e) => {
    const responsibilitiesText = e.target.value;
    setProjectForm(prev => ({
      ...prev,
      responsibilities: responsibilitiesText
    }));
  };

  // Get responsibilities as text for the form
  const getResponsibilitiesText = (project) => {
    if (!project || !project.responsibilities || project.responsibilities.length === 0) {
      return '';
    }

    return project.responsibilities
      .map(r => typeof r === 'string' ? r : r.responsibility)
      .join('\n');
  };

  // Handle technologies input
  const handleTechnologiesChange = (e) => {
    const technologiesText = e.target.value;
    setProjectForm(prev => ({
      ...prev,
      technologies: technologiesText
    }));
  };

  // Get technologies as text for the form
  const getTechnologiesText = (project) => {
    if (!project || !project.technologies || project.technologies.length === 0) {
      return '';
    }

    return project.technologies
      .map(t => typeof t === 'string' ? t : t.technology_name)
      .join(', ');
  };

  // Add new project
  const handleAddProject = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!projectForm.project_title || !projectForm.start_date ||
      (!projectForm.end_date && !projectForm.is_current)) {
      setFormError('Please fill out all required fields');
      return;
    }

    try {
      // Format data for API
      const projectData = {
        project_title: projectForm.project_title,
        start_date: projectForm.start_date,
        end_date: projectForm.is_current ? null : projectForm.end_date,
        is_current: projectForm.is_current,
        url: projectForm.url || null
      };

      // Create the project
      const createdProject = await addProject(projectData, profileData?.profile_id);

      // Add responsibilities
      const responsibilities = projectForm.responsibilities
        .split('\n')
        .filter(resp => resp.trim())
        .map(resp => resp.trim());

      for (const respText of responsibilities) {
        await addProjectResponsibility(createdProject.id, { responsibility: respText }, profileData?.profile_id);
      }

      // Add technologies
      const technologies = projectForm.technologies
        .split(',')
        .filter(tech => tech.trim())
        .map(tech => tech.trim());

      for (const techName of technologies) {
        await addProjectTechnology(createdProject.id, { technology_name: techName }, profileData?.profile_id);
      }

      // Fetch all projects to get the updated list with all relationships
      const updatedProjects = await getProjects(profileData?.profile_id);
      setProjectsList(updatedProjects);

      // Reset form and state
      clearForm();
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding project:', error);
      setFormError('Failed to add project. Please try again.');
    }
  };

  // Start editing a project
  const handleEditStart = (project) => {
    setIsEditing(project.id);

    setProjectForm({
      project_title: project.project_title,
      start_date: project.start_date,
      end_date: project.end_date || '',
      is_current: project.is_current,
      technologies: getTechnologiesText(project),
      responsibilities: getResponsibilitiesText(project),
      url: project.url || ''
    });
  };

  // Save edited project
  const handleEditSave = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!projectForm.project_title || !projectForm.start_date ||
      (!projectForm.end_date && !projectForm.is_current)) {
      setFormError('Please fill out all required fields');
      return;
    }

    try {
      // Format data for API
      const projectData = {
        project_title: projectForm.project_title,
        start_date: projectForm.start_date,
        end_date: projectForm.is_current ? null : projectForm.end_date,
        is_current: projectForm.is_current,
        url: projectForm.url || null
      };

      // Update the basic project information
      await updateProject(isEditing, projectData, profileData?.profile_id);

      // Get the existing project to compare responsibilities and technologies
      const existingProject = projectsList.find(proj => proj.id === isEditing);

      // Handle responsibilities
      const existingResponsibilities = existingProject?.responsibilities || [];
      const newResponsibilities = projectForm.responsibilities
        .split('\n')
        .filter(resp => resp.trim())
        .map(resp => resp.trim());

      // Delete removed responsibilities
      for (const existingResp of existingResponsibilities) {
        if (!newResponsibilities.includes(existingResp.responsibility)) {
          await deleteProjectResponsibility(existingResp.id, profileData?.profile_id);
        }
      }

      // Add new responsibilities
      const existingResponsibilityTexts = existingResponsibilities.map(r => r.responsibility);
      for (const newRespText of newResponsibilities) {
        if (!existingResponsibilityTexts.includes(newRespText)) {
          await addProjectResponsibility(isEditing, { responsibility: newRespText }, profileData?.profile_id);
        }
      }

      // Handle technologies
      const existingTechnologies = existingProject?.technologies || [];
      const newTechnologies = projectForm.technologies
        .split(',')
        .filter(tech => tech.trim())
        .map(tech => tech.trim());

      // Delete removed technologies
      for (const existingTech of existingTechnologies) {
        if (!newTechnologies.includes(existingTech.technology_name)) {
          await deleteProjectTechnology(existingTech.id, profileData?.profile_id);
        }
      }

      // Add new technologies
      const existingTechnologyNames = existingTechnologies.map(t => t.technology_name);
      for (const newTechName of newTechnologies) {
        if (!existingTechnologyNames.includes(newTechName)) {
          await addProjectTechnology(isEditing, { technology_name: newTechName }, profileData?.profile_id);
        }
      }

      // Fetch all projects to get the updated list with all relationships
      const updatedProjects = await getProjects(profileData?.profile_id);
      setProjectsList(updatedProjects);

      // Reset form and state
      clearForm();
      setIsEditing(null);
    } catch (error) {
      console.error('Error updating project:', error);
      setFormError('Failed to update project. Please try again.');
    }
  };

  // Cancel adding/editing
  const handleCancel = () => {
    clearForm();
    setIsAdding(false);
    setIsEditing(null);
  };

  // Delete project
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteProject(id, profileData?.profile_id);
        setProjectsList(prev => prev.filter(proj => proj.id !== id));
      } catch (error) {
        console.error('Error deleting project:', error);
        setFormError('Failed to delete project. Please try again.');
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="projects-loading">
        <div className="loading-spinner"></div>
        <p>Loading projects...</p>
      </div>
    );
  }

  return (
    <div className="projects-section">
      <div className="section-header">
        <h2><div className="section-icon">
          {profileSection.icon}
        </div> Projects</h2>
        {!isAdding && !isEditing && (
          <button
            onClick={() => setIsAdding(true)}
            className="btn-add"
          >
            <FaPlus /> Add Project
          </button>
        )}
      </div>

      {/* Projects List */}
      {projectsList.length > 0 ? (
        <div className="projects-list">
          {projectsList.map((project) => (
            <div key={project.id} className="project-item">
              {isEditing === project.id ? (
                <form onSubmit={handleEditSave} className="project-form">
                  <h3>Edit Project</h3>
                  {formError && <div className="form-error">{formError}</div>}

                  <div className="form-group">
                    <label htmlFor="project_title">Project Title *</label>
                    <input
                      type="text"
                      id="project_title"
                      name="project_title"
                      value={projectForm.project_title}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="start_date">Start Date *</label>
                      <input
                        type="date"
                        id="start_date"
                        name="start_date"
                        value={projectForm.start_date}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="end_date">End Date {!projectForm.is_current && '*'}</label>
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        value={projectForm.end_date}
                        onChange={handleInputChange}
                        disabled={projectForm.is_current}
                        required={!projectForm.is_current}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group checkbox">
                      <div className="current-checkbox">
                        <input
                          type="checkbox"
                          id="is_current"
                          name="is_current"
                          checked={projectForm.is_current}
                          onChange={handleInputChange}
                          className="checkbox-input"
                        />
                        <label htmlFor="is_current" className="checkbox-label">This is an ongoing project</label>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="technologies">Technologies Used</label>
                    <input
                      type="text"
                      id="technologies"
                      name="technologies"
                      value={projectForm.technologies}
                      onChange={handleTechnologiesChange}
                      placeholder="e.g., React, Node.js, MongoDB (comma separated)"
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="responsibilities">Responsibilities</label>
                    <textarea
                      id="responsibilities"
                      name="responsibilities"
                      value={projectForm.responsibilities}
                      onChange={handleResponsibilitiesChange}
                      rows="4"
                      className="form-control"
                    />
                    <small className="form-text">Separate each responsibility with a new line</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="url">Project URL</label>
                    <input
                      type="url"
                      id="url"
                      name="url"
                      value={projectForm.url}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      className="form-control"
                    />
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="btn-secondary">
                      <FaTimes /> Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      <FaSave /> Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="project-display">
                  <div className="project-header">
                    <div className="project-title">
                      <h3>{project.project_title}</h3>
                      <p className="project-dates">
                        {formatDate(project.start_date)} - {project.is_current ? 'Present' : formatDate(project.end_date)}
                      </p>
                    </div>
                    <div className="project-actions">
                      <button
                        onClick={() => handleEditStart(project)}
                        className="btn-icon edit"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="btn-icon delete"
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>

                  <div className="project-details">

                    {project.technologies && project.technologies.length > 0 && (
                      <div className="project-technologies">
                        <h5>Technologies:</h5>
                        <div className="tech-tags">
                          {project.technologies.map((tech) => (
                            <span key={tech.id} className="tech-tag">
                              {tech.technology_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {project.responsibilities && project.responsibilities.length > 0 && (
                      <div className="project-responsibilities">
                        <h5>Responsibilities:</h5>
                        <ul className="responsibility-list">
                          {project.responsibilities.map((resp) => (
                            <li key={resp.id}>{resp.responsibility}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {project.url && (
                      <div className="project-url">
                        <h5>Project Link:</h5>
                        <a href={project.url} target="_blank" rel="noopener noreferrer" className="external-link">
                          <FaExternalLinkAlt className="link-icon" /> View Project
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-list">
          <p>No projects added yet.</p>
          <p>Click "Add Project" to get started.</p>
        </div>
      )}

      {/* Add Project Form */}
      {isAdding && (
        <div className="add-project">
          <form onSubmit={handleAddProject} className="project-form">
            <h3>Add New Project</h3>
            {formError && <div className="form-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="project_title">Project Title *</label>
              <input
                type="text"
                id="project_title"
                name="project_title"
                value={projectForm.project_title}
                onChange={handleInputChange}
                required
                className="form-control"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">Start Date *</label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={projectForm.start_date}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="end_date">End Date {!projectForm.is_current && '*'}</label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={projectForm.end_date}
                  onChange={handleInputChange}
                  disabled={projectForm.is_current}
                  required={!projectForm.is_current}
                  className="form-control"
                />
              </div>
              <div className="form-group checkbox">
                <div className="current-checkbox">
                  <input
                    type="checkbox"
                    id="is_current"
                    name="is_current"
                    checked={projectForm.is_current}
                    onChange={handleInputChange}
                    className="checkbox-input"
                  />
                  <label htmlFor="is_current" className="checkbox-label">This is an ongoing project</label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="technologies">Technologies Used</label>
              <input
                type="text"
                id="technologies"
                name="technologies"
                value={projectForm.technologies}
                onChange={handleTechnologiesChange}
                placeholder="e.g., React, Node.js, MongoDB (comma separated)"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="responsibilities">Responsibilities</label>
              <textarea
                id="responsibilities"
                name="responsibilities"
                value={projectForm.responsibilities}
                onChange={handleResponsibilitiesChange}
                rows="4"
                className="form-control"
              />
              <small className="form-text">Separate each responsibility with a new line</small>
            </div>

            <div className="form-group">
              <label htmlFor="url">Project URL</label>
              <input
                type="url"
                id="url"
                name="url"
                value={projectForm.url}
                onChange={handleInputChange}
                placeholder="https://example.com"
                className="form-control"
              />
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-secondary">
                <FaTimes /> Cancel
              </button>
              <button type="submit" className="btn-primary">
                <FaPlus /> Add Project
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Projects;